import { parseHHMM, type WorkCalendar } from '@hana/work-calendar';
import { leaveBalance, leaveDays, otHours, type MeePerson, type MeeRequest, type RequestType } from '../data';
import { dayNo, fdw } from '../home/view';

export const LEAVE_TYPES = ['Phép năm', 'Phép không lương', 'Việc cá nhân', 'Ốm/Khám bệnh', 'Con ốm', 'Hiếu hỉ', 'Thai sản', 'Khác'] as const;
/** loại nghỉ bắt buộc đính kèm giấy khám */
const NEEDS_PAPER: readonly string[] = ['Ốm/Khám bệnh', 'Con ốm', 'Thai sản'];
const hhmm = (m: number): string => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Khung tăng ca chọn được của ngày `day`, dựng từ ca của lịch công ty (ngày nghỉ → ca của ngày làm việc kế): sau hết ca 2h · 2h30 · lùi 30' ra 3h · cả ca sáng. */
export function otWindows(cal: WorkCalendar, day: string): string[] {
  const shifts = cal.shiftsOf(day);
  const ref = shifts.length ? shifts : cal.shiftsOf(cal.nextWorkDay(day));
  if (!ref.length) return [];
  const end = ref[ref.length - 1]![1];
  const [mStart, mEnd] = ref[0]!;
  return [[end, end + 120], [end, end + 150], [end + 30, end + 210], [mStart, mEnd]].map(([a, b]) => `${hhmm(a!)}-${hhmm(b!)}`);
}
export const FIX_KINDS = [
  ['out', 'Quên chấm ra'],
  ['site', 'Chấm ngoài văn phòng'],
  ['in', 'Quên chấm vào'],
] as const;
export type FixKind = (typeof FIX_KINDS)[number][0];
export type HalfKind = 'full' | 'AM' | 'PM' | 'HR';
/** số giờ của đơn nghỉ "Theo giờ" (như board) */
export const HOURLY_LEAVE_H = 2;

export interface RequestDraft {
  kind: RequestType;
  ltype: string;
  from: string;
  to: string;
  half: HalfKind;
  /** khung giờ tăng ca "HH:MM-HH:MM" */
  window: string;
  fix: FixKind;
  place: string;
  reason: string;
  /** đã bấm đính kèm giấy khám (giả lập) */
  paper: boolean;
  /** người nhận bàn giao (đơn nghỉ phép) */
  handover: string;
}

export function newDraft(cal: WorkCalendar, kind: RequestType, day: string): RequestDraft {
  return { kind, ltype: 'Phép năm', from: day, to: day, half: 'full', window: otWindows(cal, day)[0] ?? '', fix: 'out', place: '', reason: '', paper: false, handover: '' };
}

const nextDay = (day: string, n = 1): string => new Date((dayNo(day) + n) * 86_400_000).toISOString().slice(0, 10);

/** Các ngày chọn được trong danh sách ngày (T6 23/10 · nghỉ nếu không phải ngày làm việc). */
export function dayOptions(cal: WorkCalendar, from: string, count: number): { value: string; label: string }[] {
  return Array.from({ length: count }, (_, i) => {
    const d = nextDay(from, i);
    return { value: d, label: `${fdw(d)}${cal.isWorkDay(d) ? '' : ' · nghỉ'}` };
  });
}

export interface DraftEval {
  /** số ngày (nghỉ phép / công tác) */
  days: number;
  /** giờ tăng ca */
  ot: number;
  annual: boolean;
  /** phép còn lại sau đơn (chỉ khi Phép năm) */
  leftAfter: number;
  /** ngày nghỉ tuần / lễ trong khoảng đã bỏ */
  offDays: number;
  code: string;
  sub: string;
  /** hạn giải trình: ngày làm việc thứ 2 sau ngày cần giải trình */
  fixDue: string;
  errors: { reason?: string; range?: string; paper?: string; balance?: string; overlap?: string; time?: string };
}

/** Mã đơn kế tiếp: tiền tố loại + YYMM + số thứ tự (lớn nhất đang có + 1, mọi người cùng dãy). */
export function nextCode(kind: RequestType, requests: readonly MeeRequest[], today: string): string {
  const prefix = { leave: 'NP', ot: 'OT', trip: 'CT', fix: 'GT' }[kind];
  const ym = today.slice(2, 4) + today.slice(5, 7);
  const re = new RegExp(`^${prefix}-${ym}-(\\d+)$`);
  const max = requests.reduce((m, r) => Math.max(m, Number(re.exec(r.id)?.[1] ?? 0)), 0);
  return `${prefix}-${ym}-${String(max + 1).padStart(3, '0')}`;
}

const covers = (r: MeeRequest, from: string, to: string) => r.from <= to && (r.to ?? r.from) >= from;

/** Xem trước + kiểm hợp lệ của phiếu đang soạn. Số ngày / ngày nghỉ theo lịch công ty. */
export function evaluateDraft(
  cal: WorkCalendar,
  person: Pick<MeePerson, 'id' | 'leaveUsedBase' | 'leaveByMonth'>,
  d: RequestDraft,
  requests: readonly MeeRequest[],
  today: string,
): DraftEval {
  const from = d.from;
  const to = d.to < d.from ? d.from : d.to;
  const errors: DraftEval['errors'] = {};
  const mine = requests.filter((r) => r.owner === person.id);
  let days = 0;
  let offDays = 0;
  if (d.kind === 'leave' || d.kind === 'trip') {
    const full = leaveDays(cal, from, to);
    for (let x = from; x <= to; x = nextDay(x)) if (!cal.isWorkDay(x)) offDays++;
    if (d.kind === 'leave' && d.half === 'HR') days = from === to && full === 1 ? HOURLY_LEAVE_H / cal.dayHours(from) : 0;
    else if (d.kind === 'leave' && d.half !== 'full') days = leaveDays(cal, from, to, true);
    else days = full;
    days = Math.round(days * 100) / 100;
    if (days === 0) errors.range = d.half !== 'full' && d.kind === 'leave' ? 'Nửa ngày / theo giờ chỉ chọn trong một ngày làm việc' : 'Khoảng ngày không có ngày làm việc';
    const clash = mine.find((r) => (r.type === 'leave' || r.type === 'trip') && (r.status === 'Pending' || r.status === 'Approved') && covers(r, from, to));
    if (clash) errors.overlap = `Trùng đơn ${clash.id}`;
  }
  let ot = 0;
  if (d.kind === 'ot') {
    const [a, b] = d.window.split('-') as [string, string];
    ot = otHours({ t0: a, t1: b });
    if (parseHHMM(b) <= parseHHMM(a)) errors.time = 'Giờ kết thúc phải sau giờ bắt đầu';
    if (from > today) errors.range = 'Chỉ đăng ký tăng ca cho ngày đã tới';
  }
  if (d.kind === 'fix' && !cal.isWorkDay(from)) errors.range = 'Ngày giải trình phải là ngày làm việc';
  const annual = d.kind === 'leave' && d.ltype === 'Phép năm';
  const left = leaveBalance(cal, person, requests).leftAfter;
  const leftAfter = left - (annual ? days : 0);
  if (annual && leftAfter < 0) errors.balance = 'Vượt số phép còn lại · đổi sang Phép không lương';
  if (d.kind === 'leave' && NEEDS_PAPER.includes(d.ltype) && !d.paper) errors.paper = 'Cần đính kèm giấy khám';
  if (!d.reason.trim()) errors.reason = `Cần nhập ${d.kind === 'fix' ? 'giải trình' : 'lý do'}`;
  const sub =
    d.kind === 'leave' ? d.ltype
    : d.kind === 'trip' ? `Công tác ${d.place.trim() || 'ngoài VP'}`
    : d.kind === 'ot' ? 'Tăng ca'
    : FIX_KINDS.find((f) => f[0] === d.fix)![1];
  return {
    days,
    ot,
    annual,
    leftAfter,
    offDays,
    code: nextCode(d.kind, requests, today),
    sub,
    fixDue: cal.addWorkDays(from, 2),
    errors,
  };
}

/** Đơn Pending từ phiếu hợp lệ; `sent` như "22/Oct/2026 15:20". */
export function buildRequest(d: RequestDraft, ev: DraftEval, owner: string, sent: string): MeeRequest {
  const to = d.to < d.from ? d.from : d.to;
  const base = { id: ev.code, owner, type: d.kind, sub: ev.sub, from: d.from, status: 'Pending' as const, sent };
  if (d.kind === 'ot') {
    const [t0, t1] = d.window.split('-') as [string, string];
    return { ...base, t0, t1 };
  }
  if (d.kind === 'fix') return base;
  return { ...base, to, qty: ev.days, ...(d.kind === 'leave' && (d.half === 'AM' || d.half === 'PM') ? { half: d.half } : {}), ...(d.kind === 'leave' && d.handover ? { handover: d.handover } : {}) };
}

/** "22/Oct/2026 15:20" */
export function sentLabel(day: string, now: string, monthAbbr: string): string {
  return `${day.slice(8, 10)}/${monthAbbr}/${day.slice(0, 4)} ${now}`;
}
